// src/services/nfpApi.js
import { apiFetch } from '../api/client';

const q = (obj = {}) =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
const IS_DEV = process.env.NODE_ENV !== 'production';

function uploadKindLabel(kind = 'generic') {
  if (kind === 'logo' || kind === 'brand-logo') return 'logo';
  if (kind === 'image' || kind === 'avatar' || kind === 'post-media') return 'image';
  return 'file';
}

function friendlyUploadError(message, { kind = 'generic' } = {}) {
  const raw = String(message || '').trim();
  const label = uploadKindLabel(kind);
  const selected = label === 'logo' ? 'selected logo' : label === 'image' ? 'selected image' : 'selected file';
  const alternate = label === 'file' ? 'different file' : 'different image';

  const sizeMatch = raw.match(/File exceeds\s+(\d+)\s*MB limit\.?/i);
  if (sizeMatch) {
    const prefix = label === 'file' ? 'File' : 'Image';
    return `${prefix} too large. Please choose a file that is ${sizeMatch[1]} MB or smaller.`;
  }
  if (/unsupported content type/i.test(raw)) {
    if (label === 'logo') return 'Please upload a logo image file, such as a JPG, PNG, or WebP.';
    if (label === 'image') return 'Please upload an image file, such as a JPG, PNG, or WebP.';
    return 'Please upload a supported file type.';
  }
  if (/invalid file size|missing file|empty/i.test(raw)) {
    return label === 'file'
      ? "We couldn't read that file. Please try a different file."
      : "We couldn't read that image file. Please try a different image.";
  }
  if (/service temporarily unavailable|s3 upload provider is not enabled|s3 upload bucket is not configured/i.test(raw)) {
    return label === 'logo'
      ? 'Logo upload is temporarily unavailable. Please try again in a minute.'
      : label === 'image'
        ? 'Image upload is temporarily unavailable. Please try again in a minute.'
        : 'File upload is temporarily unavailable. Please try again in a minute.';
  }
  return `We couldn't upload the ${selected}. Please try again or choose a ${alternate}.`;
}

export const nfpApi = {
  /* ------------------ bootstrap / retrieval ------------------ */
  startRegistration: () =>
    apiFetch('/api/cause-registrations/start', { method: 'POST' }),

  listMyRegistrations: () =>
    apiFetch('/api/cause-registrations/mine', { method: 'GET' }),

  get: (id) => apiFetch(`/api/cause-registrations/${id}`, { method: 'GET' }),

  updateRegistrationMeta: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/meta`, { method: 'PATCH', body }),

  /* ------------------ per-step updates ------------------ */
  putApplicant: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/applicant`, { method: 'PUT', body }),
  updateApplicant: (id, body) =>
    nfpApi.putApplicant(id, body), // alias for backward-compat

  putEntity: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/entity`, { method: 'PUT', body }),
  updateEntity: (id, body) =>
    nfpApi.putEntity(id, body), // alias

  putProof: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/proof`, { method: 'PUT', body }),
  updateProof: (id, body) =>
    nfpApi.putProof(id, body), // alias

  putBranding: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/branding`, { method: 'PUT', body }),
  updateBranding: (id, body) =>
    nfpApi.putBranding(id, body), // alias

  putSupporterSnapshot: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/supporter-snapshot`, { method: 'PUT', body }),
  updateSupporterSnapshot: (id, body) =>
    nfpApi.putSupporterSnapshot(id, body), // alias

  putLocations: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/locations`, { method: 'PUT', body }),
  updateLocations: (id, body) =>
    nfpApi.putLocations(id, body), // alias

  putAgreements: (id, body) =>
    apiFetch(`/api/cause-registrations/${id}/agreements`, { method: 'PUT', body }),
  updateAgreements: (id, body) =>
    nfpApi.putAgreements(id, body), // alias

  /* ------------------ submit ------------------ */
  submit: (id) =>
    apiFetch(`/api/cause-registrations/${id}/submit`, { method: 'POST' }),

  /* ------------------ helpers used by Step 1 / Step 2 ------------------ */
  checkEmailExists: (email) =>
    apiFetch(`/api/auth/exists?${q({ email })}`, { method: 'GET' }),

  checkOrgDuplicate: (payload) =>
    apiFetch('/api/causes/dupe-check', { method: 'POST', body: payload }),

  /* ------------------ asset upload (logo/images) ------------------ */
  uploadAsset: async (file, { kind = 'generic', folder } = {}) => {
    const filename = file?.name || 'upload.bin';
    const contentType = file?.type || 'application/octet-stream';
    const size = Number(file?.size || 0);
    let presign;
    try {
      presign = await apiFetch('/api/uploads/presign', {
        method: 'POST',
        body: { kind, folder, filename, contentType, size },
      });
    } catch (err) {
      if (err?.status === 503 && IS_DEV) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', kind);
        if (folder) fd.append('folder', folder);
        return apiFetch('/api/uploads', { method: 'POST', body: fd });
      }
      if (err?.status === 503) {
        throw new Error(friendlyUploadError(err?.data?.error || err?.message, { kind }));
      }
      throw new Error(friendlyUploadError(err?.data?.error || err?.message, { kind }));
    }

    if (!presign?.uploadUrl) {
      throw new Error(friendlyUploadError('Missing upload URL from server', { kind }));
    }

    let putRes;
    try {
      putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: {
          ...(presign.headers || {}),
          'Content-Type': contentType,
        },
        body: file,
      });
    } catch (err) {
      throw new Error(friendlyUploadError(err?.message, { kind }));
    }

    if (!putRes.ok) {
      const raw = await putRes.text().catch(() => '');
      throw new Error(friendlyUploadError(raw || 'Direct upload failed', { kind }));
    }

    let complete;
    try {
      complete = await apiFetch('/api/uploads/complete', {
        method: 'POST',
        body: {
          key: presign.key,
          fileUrl: presign.fileUrl,
          kind,
        },
      });
    } catch (err) {
      throw new Error(friendlyUploadError(err?.data?.error || err?.message, { kind }));
    }

    return {
      ...complete,
      key: presign.key,
      url: presign.fileUrl,
      location: presign.fileUrl,
      name: filename,
      mime: contentType,
      size,
      id: presign.key,
      _id: presign.key,
    };
  },
};
